<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../../configs/text';
import { Vue3Lottie } from 'vue3-lottie'
import { UserRole } from '../../../../types/user';
import { useClassStore, useQuizStore, useSignalRStore, useUserStore } from '../../../../stores';
import { AnswerResultType, AssessmentAnswerDetail, AssessmentQuestion, AssessmentType, CountdownType } from '../../../../types/assessment';
import { QuestionState } from '../../../../types/quiz';

import Countdown from '../../../Assessment/Countdown.vue'
import LeaderBoard from './components/LeaderBoard.vue';
import StudentPointChanged from '../../../Assessment/StudentPointChanged.vue';
import clockSpining from '../../../../assets/animations/clock_spining.json'
import Slide from '../../../Animation/Slide.vue';
import Fade from '../../../Animation/Fade.vue';

import QuizReview from './QuizReview.vue';
import QuizPlay from './QuizPlay.vue';

const { t } = useI18n()
const quizStore = useQuizStore();
const signalr = useSignalRStore();
const userStore = useUserStore();
const classStore = useClassStore();
const isTimesUp = ref<boolean>(false)
const curentQuestion = computed<AssessmentQuestion | undefined>(() => {
    return quizStore.quiz.questions.find((question) => question.id == quizStore.questionState?.id)
})

const studentAnswer = computed(() => {
    const currentQuestionResult = classStore.currentStudent?.quizQuestion?.find((question) => question.id === quizStore.questionState?.id)
    const isCorrect = isAllCorrected(currentQuestionResult?.answer, currentQuestionResult?.correctAnswers)
    const totalPoints = isCorrect === AnswerResultType.CORRECT ? curentQuestion.value?.point : 0
    // const totalPoints = currentQuestionResult?.answer.reduce((acc, answer) => {
    //     return acc + (answer.point || 0)
    // }, 0);
    return { totalPoints, isCorrect }
})

function isAllCorrected(answers: AssessmentAnswerDetail[] | undefined, correctAnswers: string[] | undefined) {
    if (answers && correctAnswers) {
        const studentCorrectAnswers = answers.filter(answer => answer.isCorrect === 1);
        const allCorrect = correctAnswers.length > 0 && studentCorrectAnswers.length === correctAnswers.length;
        if (allCorrect) return AnswerResultType.CORRECT;
        return AnswerResultType.INCORRECT;
    }
}
function submitAnswer(answers: string[]) {
    if (userStore.userRole === UserRole.teacher) return;
    if (curentQuestion.value) {
        signalr.answerQuestion(curentQuestion.value.id, curentQuestion.value.setId, AssessmentType.QUIZ, quizStore.questionState?.startTime ?? 0, answers)
    }
}

</script>

<template>
    <div class="relative flex flex-col justify-between h-full w-full overflow-hidden" :key="quizStore.questionState?.state" v-if="quizStore.quiz && curentQuestion?.id && quizStore.questionState">
        <Fade>
            <div class="flex items-start h-12 pt-3 pl-10 gap-3" v-if="quizStore.questionState?.state === QuestionState.ON_GOING">
                <div class="relative w-full mt-2 rounded-full">
                    <Vue3Lottie class="absolute -left-4 -top-3" ref="lottieContainer" :animationData="clockSpining" :width="40" :height="40" />
                    <Countdown :type="CountdownType.BAR" :duration="curentQuestion?.questionTime ?? 0" :current="quizStore.timeRemaining" @finished="isTimesUp = true" :key="curentQuestion?.id">
                    </Countdown>
                </div>
                <template v-if="classStore.currentStudent">
                    <svg width="109" height="45" viewBox="0 0 109 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M92.8594 38.4292L108.529 44.5892V26.4892L106.469 22.4492L92.8594 38.4292Z" fill="#F65766" />
                        <path d="M108.529 26.49L92.8594 38.43V24.5L108.529 26.49Z" fill="#D33554" />
                        <path d="M108.52 0.839844V26.4898H0L6.73999 13.6698L0 0.839844H108.52Z" fill="#F65766" />
                        <text x="10" y="19" class="font-bold" fill="#FFFFFF">{{
                            `${quizStore.studentTotalPoint(classStore.currentStudent) - (studentAnswer.totalPoints ??
                                0)}
                            ${t(TEXT.points)}` }}</text>
                    </svg>
                </template>
            </div>
            <div class="w-full bg-cyan-500 dark:bg-purple-800 text-center font-bold text-lg text-white p-2 rounded-b-2xl" v-if="quizStore.questionState?.state === QuestionState.REVIEW">
                {{ t(TEXT.correct_answer) }}
            </div>
        </Fade>
        <Fade>
            <div class="flex flex-col w-full h-full gap-5 overflow-hidden" v-if="quizStore.questionState.state === QuestionState.ON_GOING">
                <QuizPlay :question="curentQuestion" :disabled="isTimesUp || quizStore.timeRemaining <= 0" @submit="submitAnswer" />
            </div>
            <div class="flex flex-col w-full h-full gap-5 overflow-hidden" v-if="quizStore.questionState.state === QuestionState.REVIEW || (quizStore.questionState.state === QuestionState.FINISHED && quizStore.showStudentPoint)">
                <QuizReview :question="curentQuestion" />
            </div>
        </Fade>

        <template v-if="quizStore.questionState?.state === QuestionState.FINISHED">
            <Slide direction="down">
                <div class="hidden" v-if="quizStore.showStudentPoint">
                    <Countdown :type="CountdownType.BAR" :duration="4" @finished="quizStore.showStudentPoint = false" :key="curentQuestion?.id">
                    </Countdown>
                </div>
                <div class="p-10 h-full overflow-auto" v-if="!quizStore.showStudentPoint">
                    <LeaderBoard />
                </div>
            </Slide>
        </template>
        <StudentPointChanged :point="studentAnswer.totalPoints ? parseFloat(studentAnswer.totalPoints.toFixed(2)) : 0" :type="studentAnswer.isCorrect ?? AnswerResultType.INCORRECT"
            v-if="quizStore.questionState?.state === QuestionState.FINISHED && quizStore.showStudentPoint" />
    </div>
</template>